/** The single kind-to-viewer dispatch table (#11712).
 *
 * There used to be two: one here in the Documents viewer, one in the RMS
 * interaction page's `DocumentViewer`. Each was an if-chain ending in a bare
 * "Unable to preview this file", so a kind either copy had not been taught fell
 * through with no error, no alert and no failing test. The RMS copy was missing
 * markdown, HTML, CSV and the MIME fallback, which is why markdown notes were
 * unrenderable there while rendering perfectly in Documents.
 *
 * The fix is the type, not the entries: `Record<ViewerKind, …>` makes a kind
 * without a renderer a compile error. Do not soften this into a `Partial`, a
 * `switch` with a `default`, or a lookup with a `??` fallback — each of those
 * restores exactly the silent fallthrough this file exists to delete.
 *
 * Presentation deliberately stays with the callers: the interaction page wraps
 * these in its zoom controls, the Documents viewer in its interaction banner and
 * citation interceptor. Only the dispatch is shared.
 */

import React, { Suspense, useEffect, useMemo, useState } from "react";

import PdfReader from "@/components/PdfReader";
import EmailViewer from "@/components/EmailViewer";
import HtmlViewer from "@/components/HtmlViewer";
import MarkdownViewer from "@/components/MarkdownViewer";
import OneNotePageViewer from "@/components/OneNoteViewer/OneNotePageViewer";
import EvestmentPageViewer from "@/components/EvestmentViewer/EvestmentPageViewer";
import TeamsPostViewer from "@/components/TeamsPostViewer/TeamsPostViewer";
import AltviaInteractionViewer from "@/components/AltviaInteractionViewer";
import FPNoteViewer from "@/components/FPNoteViewer";
import AgentNoteView, {
  agentNoteBody,
} from "@/components/FPNoteViewer/AgentNoteView";
import DocxViewer from "../common/DocxViewer";
import PptxViewer from "../common/PptxViewer";
import { ResponseWrapper } from "./services/useFileLoader";
import type { ViewerKind } from "./services/fileViewerInterface";
import { unreadableMessage } from "./unreadableMessage";

const LazyFortuneSheetExcelViewer = React.lazy(
  () => import("@/components/ExcelViewer/FortuneSheetExcelViewer")
);

/** Everything a renderer may need. Passed as one object so an arm can take what
 *  it needs and adding an input later doesn't touch all seventeen entries. */
export interface ViewerContext {
  /** The fetched bytes. Undefined while loading, or when the viewer fetches its
   *  own content (markdown and HTML re-fetch by id). */
  response?: ResponseWrapper;
  fileUrl: string;
  fileId: string;
  /** Set only for a note-only interaction, which has no blob (#11496). */
  interactionId?: string;
  /** Documents-viewer only: the overlay closes on Escape from inside the PDF. */
  onEscape?: () => void;
  /** Documents-viewer only: deep-link to a named sheet in a workbook. */
  sheetName?: string;
  /** The host pane's Download (the stored file). A viewer's own Download
   *  (the PDF reader's ⋯ menu and Ctrl/Cmd-S, the spreadsheet's can't-preview
   *  state) runs this one, never a save of the preview it holds. */
  onDownload?: () => void;
  /** Why an unreadable file cannot be read (#11277), when the opener knew;
   *  the `unreadable` arm names it. */
  unreadableReason?: "encrypted" | "no_signature" | null;
  /** The stored name, for the arm that names the format it claims. */
  fileName?: string;
}

const Spreadsheet = ({
  response,
  fileUrl,
  sheetName,
  onDownload,
}: ViewerContext) => (
  <Suspense fallback={<div className="p-6">Loading...</div>}>
    <LazyFortuneSheetExcelViewer
      fileUrl={fileUrl}
      sheetName={sheetName}
      fileContentResponse={response}
      onDownload={onDownload}
    />
  </Suspense>
);

const Note = ({ response, fileId, interactionId }: ViewerContext) => (
  // The row identity remains explicit when several interactions share a blob.
  <FPNoteViewer
    response={response}
    blobId={fileId || undefined}
    interactionId={interactionId}
  />
);

/** Renders whatever the response's own MIME type says it is. This is the arm
 *  that covers images, video, audio and plain text — files with no dedicated
 *  viewer but which the browser can display perfectly well. */
export const MimeBasedViewer = ({
  response,
}: {
  response: ResponseWrapper;
}) => {
  const mimeType = response.contentType.split(";")[0].trim();
  const category = mimeType.split("/")[0];

  const objectUrl = useMemo(() => {
    const blob = new Blob([response.payload], { type: mimeType });
    return URL.createObjectURL(blob);
  }, [response.payload, mimeType]);

  useEffect(() => {
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  if (category === "image") {
    return (
      <div className="h-full w-full flex items-center justify-center p-4">
        <img src={objectUrl} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  if (category === "video") {
    return (
      <div className="h-full w-full flex items-center justify-center p-4">
        <video src={objectUrl} controls className="max-w-full max-h-full" />
      </div>
    );
  }

  if (category === "audio") {
    return (
      <div className="h-full w-full flex items-center justify-center p-4">
        <audio src={objectUrl} controls />
      </div>
    );
  }

  if (
    category === "text" ||
    mimeType === "application/json" ||
    mimeType === "application/xml"
  ) {
    return <TextViewer response={response} />;
  }

  return (
    <div className="h-full w-full flex items-center justify-center text-gray-500">
      Unable to preview this file
    </div>
  );
};

/** An agent's `.fp` meeting note (#12284): the note body in the sandboxed,
 *  styled frame, or the file's text when it is not note JSON. */
const AgentNote = ({ response }: { response: ResponseWrapper }) => {
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    response.text().then((value) => {
      if (!cancelled) setText(value);
    });
    return () => {
      cancelled = true;
    };
  }, [response]);

  if (text === null) return <div className="p-6">Loading...</div>;
  const body = agentNoteBody(text);
  return body === null ? (
    <pre className="p-6 whitespace-pre-wrap break-words text-sm font-mono text-gray-800">
      {text}
    </pre>
  ) : (
    <AgentNoteView body={body} title="Meeting note" />
  );
};

const TextViewer = ({ response }: { response: ResponseWrapper }) => {
  const [text, setText] = useState("Loading...");

  useEffect(() => {
    response.text().then(setText);
  }, [response]);

  return (
    <pre className="p-6 whitespace-pre-wrap break-words text-sm font-mono text-gray-800">
      {text}
    </pre>
  );
};

/** One entry per `ViewerKind`. The `Record` is the guarantee: adding a kind to
 *  `ViewerKindSchema` without adding it here does not compile. */
export const VIEWER_REGISTRY: Record<
  ViewerKind,
  (ctx: ViewerContext) => React.ReactNode
> = {
  pdf: ({ response, fileUrl, onEscape, onDownload }) => (
    <PdfReader
      fileContentResponse={response}
      fileUrl={fileUrl}
      onEscape={onEscape}
      onDownload={onDownload}
    />
  ),
  email: ({ fileUrl }) => <EmailViewer fileUrl={fileUrl} />,
  excel: (ctx) => <Spreadsheet {...ctx} />,
  csv: (ctx) => <Spreadsheet {...ctx} />,
  artifact: (ctx) => <Spreadsheet {...ctx} />,
  docx: ({ response, fileUrl }) => (
    <DocxViewer fileUrl={fileUrl} fileContentResponse={response} />
  ),
  pptx: ({ response, fileUrl }) => (
    <PptxViewer fileUrl={fileUrl} fileContentResponse={response} />
  ),
  "onenote-page": ({ response }) => <OneNotePageViewer response={response} />,
  "evestment-note": ({ response }) => (
    <EvestmentPageViewer response={response} />
  ),
  "teams-post": ({ response }) => <TeamsPostViewer response={response} />,
  altvia_interaction: ({ fileUrl }) => (
    <AltviaInteractionViewer fileUrl={fileUrl} />
  ),
  altvia_interaction_note: (ctx) => <Note {...ctx} />,
  "finpilot-interaction": (ctx) => <Note {...ctx} />,
  "agent-note": ({ response }) =>
    response ? (
      <AgentNote response={response} />
    ) : (
      <UnrenderableFile reason="This note could not be loaded." />
    ),
  html: ({ fileId }) => <HtmlViewer fileId={fileId} />,
  markdown: ({ fileId }) => <MarkdownViewer fileId={fileId} />,
  unsupported: ({ response }) =>
    response ? (
      <MimeBasedViewer response={response} />
    ) : (
      <UnrenderableFile reason="This file type can't be previewed." />
    ),
  unreadable: ({ unreadableReason, fileName }) => (
    <UnrenderableFile reason={unreadableMessage(unreadableReason, fileName)} />
  ),
  empty: () => <UnrenderableFile reason="This file is empty." />,
};

/** The honest end state: we know what the file is and have nothing to show for
 *  it. Distinct from `UnrecognizedKind`, which means we do not know what it is —
 *  conflating those two is what made #11712 invisible for four weeks. */
export const UnrenderableFile = ({ reason }: { reason: string }) => (
  <div className="h-full w-full flex items-center justify-center text-gray-500">
    {reason}
  </div>
);

/** The preview endpoint declared a kind this build does not know. That is our
 *  bug, not a property of the file, so it says so plainly instead of hiding
 *  behind "Unable to preview this file" — the wording that let #11712 and
 *  #11328 both read as "this file just isn't previewable". `useFileLoader` has
 *  already reported it to Sentry by the time this renders. */
export const UnrecognizedKind = ({ kind }: { kind: string }) => (
  <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-gray-500">
    <div>This file can&apos;t be previewed in this version of the app.</div>
    <div className="font-mono text-xs text-gray-400">
      Unrecognized file kind: {kind}
    </div>
  </div>
);
